
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiLoader,
  FiMinus,
  FiPlus,
  FiRefreshCw
} from 'react-icons/fi';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { foundStatusLabel } from './ZoneCountFoundList';
import { lotLine } from './ZoneCountProductCard';
import type {
  BulkVariance,
  CountSummary,
  FoundUnit,
  ReviewSelection,
  ZoneCountReview as Review
} from '../zoneCountSteps';

type ZoneCountReviewProps = {
  review: Review;
  summary: CountSummary;
  selection: ReviewSelection;
  onSelectionChange: (update: (current: ReviewSelection) => ReviewSelection) => void;
  onRetryLookup: (key: string) => void;
  note: string;
  onNoteChange: (note: string) => void;
};

type FoundChoice = ReviewSelection['found'][string];

function QtyStepper({
  value,
  max,
  label,
  onChange
}: {
  value: number;
  max: number;
  label: string;
  onChange: (value: number) => void;
}) {
  const set = (n: number) => onChange(Math.min(max, Math.max(0, Number.isFinite(n) ? Math.trunc(n) : 0)));
  return (
    <div className="zc-bulk__stepper zc-bulk__stepper--small">
      <button type="button" aria-label="One less" disabled={value <= 0} onClick={() => set(value - 1)}>
        <FiMinus />
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={max}
        aria-label={label}
        value={value}
        onChange={(e) => set(parseInt(e.target.value, 10))}
      />
      <button type="button" aria-label="One more" disabled={value >= max} onClick={() => set(value + 1)}>
        <FiPlus />
      </button>
    </div>
  );
}

function FoundChoices({
  found,
  choice,
  onChoose
}: {
  found: FoundUnit;
  choice: FoundChoice | undefined;
  onChoose: (choice: FoundChoice) => void;
}) {
  const lookup = found.lookup!;
  const options: { value: FoundChoice; label: string }[] = [
    ...(lookup.canQuarantine ? [{ value: 'QUARANTINE' as const, label: 'Quarantine' }] : []),
    ...(lookup.canMoveHere ? [{ value: 'MOVE_HERE' as const, label: 'Record here' }] : []),
    { value: 'SKIP' as const, label: 'Leave' }
  ];
  return (
    <div className="zc-segmented" role="radiogroup" aria-label={`What to do with serial ${found.serialNumber}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={(choice ?? 'SKIP') === option.value}
          className={['zc-segmented__option', (choice ?? 'SKIP') === option.value && 'active']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onChoose(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function VarianceRow({
  variance,
  selected,
  onChange
}: {
  variance: BulkVariance;
  selected: number;
  onChange: (value: number) => void;
}) {
  const amount = Math.abs(variance.variance);
  const short = variance.variance < 0;
  return (
    <div className={['zc-review-row', variance.drift && 'zc-review-row--blocked'].filter(Boolean).join(' ')}>
      <div className="zc-review-row__main">
        <span className="zc-review-row__name">{variance.product.productName}</span>
        <span className="zc-review-row__meta">
          {variance.lot
            ? lotLine(variance.lot)
            : `Lot ${variance.lotNumber || '—'} · Exp ${formatDateOnly(variance.expiration) || '—'}`}
        </span>
        <span className="zc-review-row__figures">
          {variance.lot ? `Record ${variance.expected} · ` : 'Not on record · '}
          Counted {variance.counted} ·{' '}
          <strong className={short ? 'zc-text-short' : 'zc-text-over'}>
            {amount} {short ? 'short' : 'over'}
          </strong>
        </span>
        {variance.drift && (
          <span className="zc-review-row__blocked">
            <FiAlertTriangle aria-hidden /> The records for this lot disagree — reconcile the zone first.
          </span>
        )}
      </div>
      {!variance.drift && (
        <div className="zc-review-row__control">
          <small>Quarantine</small>
          <QtyStepper
            value={selected}
            max={amount}
            label={`Units of ${variance.product.productName} to quarantine`}
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The end of a count: how the shelf compares with the records, and what to do about each
 * difference. Everything the count can act on starts selected — the review is for taking things
 * OUT (a unit found after all, a short lot that is on its way back) — and nothing is written until
 * the operator confirms below.
 */
function ZoneCountReview({
  review,
  summary,
  selection,
  onSelectionChange,
  onRetryLookup,
  note,
  onNoteChange
}: ZoneCountReviewProps) {
  const selectedMissing = new Set(selection.missing);
  const actionableMissing = review.missing
    .filter((m) => !m.lot.drift)
    .flatMap((m) => m.units.map((u) => u.serializedItemId));
  const allMissingSelected =
    actionableMissing.length > 0 && actionableMissing.every((id) => selectedMissing.has(id));
  const shortTotal =
    review.missingCount + review.short.reduce((sum, v) => sum + Math.abs(v.variance), 0);
  const overTotal =
    review.found.length + review.over.reduce((sum, v) => sum + Math.abs(v.variance), 0);

  const toggleMissing = (id: string) =>
    onSelectionChange((current) => ({
      ...current,
      missing: current.missing.includes(id)
        ? current.missing.filter((x) => x !== id)
        : [...current.missing, id]
    }));

  return (
    <div className="zc-review">
      <div className="zc-tiles">
        <div className="zc-tile">
          <span className="zc-tile__value">{summary.expected}</span>
          <span className="zc-tile__label">On record</span>
        </div>
        <div className="zc-tile">
          <span className="zc-tile__value">{summary.counted}</span>
          <span className="zc-tile__label">Counted</span>
        </div>
        <div className={['zc-tile', shortTotal > 0 && 'zc-tile--short'].filter(Boolean).join(' ')}>
          <span className="zc-tile__value">{shortTotal}</span>
          <span className="zc-tile__label">Short</span>
        </div>
        <div className={['zc-tile', overTotal > 0 && 'zc-tile--over'].filter(Boolean).join(' ')}>
          <span className="zc-tile__value">{overTotal}</span>
          <span className="zc-tile__label">Over</span>
        </div>
      </div>

      {review.matches && (
        <div className="zc-matches">
          <FiCheckCircle aria-hidden />
          <div>
            <strong>The shelf matches the records</strong>
            <span>Every unit on record was counted, and nothing extra was found.</span>
          </div>
        </div>
      )}

      {review.notCounted.length > 0 && (
        <div className="zc-callout zc-callout--warn">
          <FiAlertTriangle aria-hidden />
          <div>
            <strong>
              {review.notCounted.length} lot{review.notCounted.length === 1 ? '' : 's'} not counted
            </strong>
            <span>
              {review.notCounted
                .map(({ product, lot }) => `${product.productName} lot ${lot.lot || '—'}`)
                .join(', ')}
              . No number was entered, so {review.notCounted.length === 1 ? 'it is' : 'they are'} left
              out of this review. Go back to count {review.notCounted.length === 1 ? 'it' : 'them'}.
            </span>
          </div>
        </div>
      )}

      {review.controlledShort.length > 0 && (
        <div className="zc-callout zc-callout--danger">
          <FiAlertTriangle aria-hidden />
          <div>
            <strong>Controlled substances short</strong>
            <span>
              {review.controlledShort.join(', ')}. A theft or significant loss of a controlled
              substance must be reported to the DEA (Form 106) — tell your compliance lead.
            </span>
          </div>
        </div>
      )}

      {review.missing.length > 0 && (
        <section className="zc-review-section" aria-label="Missing serial numbers">
          <div className="zc-section-head">
            <h3>Missing — on record, not found</h3>
            <span className="zc-pill zc-pill--short">{review.missingCount}</span>
            {actionableMissing.length > 1 && (
              <button
                type="button"
                className="zc-link"
                onClick={() =>
                  onSelectionChange((current) => ({
                    ...current,
                    missing: allMissingSelected ? [] : actionableMissing
                  }))
                }
              >
                {allMissingSelected ? 'Select none' : 'Select all'}
              </button>
            )}
          </div>
          <p className="zc-review-section__hint">
            Checked serials leave this zone’s record for the quarantine zone, so they can be
            investigated.
          </p>
          {review.missing.map(({ product, lot, units }) => (
            <div key={lot.key} className="zc-review-group">
              <div className="zc-review-group__head">
                <span className="zc-review-row__name">{product.productName}</span>
                <span className="zc-review-row__meta">{lotLine(lot)}</span>
                {lot.drift && (
                  <span className="zc-review-row__blocked">
                    <FiAlertTriangle aria-hidden /> The records for this lot disagree — reconcile the
                    zone first.
                  </span>
                )}
              </div>
              <ul className="zc-serials">
                {units.map((unit) => (
                  <li key={unit.serializedItemId}>
                    <label className={['zc-check', lot.drift && 'zc-check--disabled'].filter(Boolean).join(' ')}>
                      <input
                        type="checkbox"
                        disabled={lot.drift}
                        checked={selectedMissing.has(unit.serializedItemId)}
                        onChange={() => toggleMissing(unit.serializedItemId)}
                      />
                      <span className="zc-mono">{unit.serialNumber}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {review.found.length > 0 && (
        <section className="zc-review-section" aria-label="Found serial numbers">
          <div className="zc-section-head">
            <h3>Found — not on record here</h3>
            <span className="zc-pill zc-pill--over">{review.found.length}</span>
          </div>
          <p className="zc-review-section__hint">
            Quarantine sets the unit aside for investigation. A unit recorded in another zone can
            instead be recorded here, if it was simply shelved in the wrong place.
          </p>
          {review.found.map((found) => (
            <div
              key={found.key}
              className={[
                'zc-review-row',
                found.lookup && !found.lookup.canQuarantine && !found.lookup.canMoveHere && 'zc-review-row--blocked'
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="zc-review-row__main">
                <span className="zc-review-row__name">
                  {found.lookup?.product?.productName || 'Serialized unit'}
                </span>
                <span className="zc-mono">Serial {found.serialNumber}</span>
                <span className="zc-review-row__meta">
                  {!found.lookup && !found.lookupFailed && <FiLoader aria-hidden className="zc-spin" />}
                  {found.lookup ? found.lookup.message : foundStatusLabel(found)}
                </span>
              </div>
              <div className="zc-review-row__control">
                {found.lookup && (found.lookup.canQuarantine || found.lookup.canMoveHere) ? (
                  <FoundChoices
                    found={found}
                    choice={selection.found[found.key]}
                    onChoose={(choice) =>
                      onSelectionChange((current) => ({
                        ...current,
                        found: { ...current.found, [found.key]: choice }
                      }))
                    }
                  />
                ) : found.lookupFailed ? (
                  <button type="button" className="zc-link" onClick={() => onRetryLookup(found.key)}>
                    <FiRefreshCw aria-hidden /> Check again
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </section>
      )}

      {review.short.length > 0 && (
        <section className="zc-review-section" aria-label="Counted short">
          <div className="zc-section-head">
            <h3>Counted short</h3>
            <span className="zc-pill zc-pill--short">
              {review.short.reduce((sum, v) => sum + Math.abs(v.variance), 0)}
            </span>
          </div>
          <p className="zc-review-section__hint">
            The shortfall leaves this zone’s record for the quarantine zone, where it can be
            investigated.
          </p>
          {review.short.map((variance) => (
            <VarianceRow
              key={variance.key}
              variance={variance}
              selected={selection.bulk[variance.key] ?? 0}
              onChange={(value) =>
                onSelectionChange((current) => ({
                  ...current,
                  bulk: { ...current.bulk, [variance.key]: value }
                }))
              }
            />
          ))}
        </section>
      )}

      {review.over.length > 0 && (
        <section className="zc-review-section" aria-label="Counted over">
          <div className="zc-section-head">
            <h3>Counted over</h3>
            <span className="zc-pill zc-pill--over">
              {review.over.reduce((sum, v) => sum + Math.abs(v.variance), 0)}
            </span>
          </div>
          <p className="zc-review-section__hint">
            The extra units are recorded in the quarantine zone — move them there.
          </p>
          {review.over.map((variance) => (
            <VarianceRow
              key={variance.key}
              variance={variance}
              selected={selection.bulk[variance.key] ?? 0}
              onChange={(value) =>
                onSelectionChange((current) => ({
                  ...current,
                  bulk: { ...current.bulk, [variance.key]: value }
                }))
              }
            />
          ))}
        </section>
      )}

      {!review.matches && (
        <label className="zc-field zc-review__note">
          <span>Note for the record (optional)</span>
          <textarea
            rows={2}
            maxLength={300}
            placeholder="e.g. Shelf damaged by a leak"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
          />
        </label>
      )}
    </div>
  );
}

export default ZoneCountReview;
